import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Center } from '../../interfaces/center';
import { BackfillResult, CenterService } from '../../services/center.service';
import { AuthService } from '../../auth/auth.service';
import { Permission } from '../../interfaces/permission';

@Component({
  selector: 'app-centers-management',
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule
  ],
  templateUrl: './centers-management.component.html',
  styleUrl: './centers-management.component.scss'
})
export class CentersManagementComponent implements OnInit {
  centers: Center[] = [];
  dataSource = new MatTableDataSource<Center>([]);
  displayedColumns: string[] = ['name', 'oimName', 'fullName', 'scoring', 'maxABO', 'maxHLA', 'maxABOAndHLA', 'participantCounts', 'actions'];
  editingCenter: Center | null = null;
  centerForm!: FormGroup;
  canEditCenters = false;
  loading = true;
  backfillRunning = false;
  backfillResult: BackfillResult | null = null;

  constructor(
    private centerService: CenterService,
    private authService: AuthService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.centerForm = this.fb.group({
      id: [''],
      scoring: [0],
      MaxABOIncompatibleTransplants: [3, [Validators.required, Validators.min(0)]],
      MaxHLAIncompatibleTransplants: [3, [Validators.required, Validators.min(0)]],
      MaxABOAndHLAIncompatibleTransplants: [3, [Validators.required, Validators.min(0)]]
    });
  }

  ngOnInit(): void {
    this.checkPermissions();
    this.loadCenters();
  }

  checkPermissions(): void {
    this.authService.getAllPermissions$().subscribe((permissions) => {
      this.canEditCenters = permissions.includes(Permission.CENTERS_EDIT);
    });
  }

  loadCenters(): void {
    this.loading = true;
    this.centerService.getAllCenters().subscribe({
      next: (centers) => {
        // Sort centers by fullName
        this.centers = centers.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
        this.dataSource.data = this.centers;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading centers:', error);
        this.showNotification('Failed to load centers');
        this.loading = false;
      }
    });
  }

  startEditing(center: Center): void {
    if (!this.canEditCenters) {
      this.showNotification('You do not have permission to edit centers');
      return;
    }

    this.editingCenter = { ...center };
    this.centerForm.patchValue({
      id: center.id,
      scoring: center.scoring || 0,
      MaxABOIncompatibleTransplants: center.MaxABOIncompatibleTransplants ?? 3,
      MaxHLAIncompatibleTransplants: center.MaxHLAIncompatibleTransplants ?? 3,
      MaxABOAndHLAIncompatibleTransplants: center.MaxABOAndHLAIncompatibleTransplants ?? 3
    });
  }

  saveCenter(): void {
    if (this.centerForm.valid && this.editingCenter) {
      const formValue = this.centerForm.getRawValue();
      const updatedCenter: Center = {
        ...this.editingCenter,
        ...formValue
      };

      this.centerService.updateCenter(updatedCenter).subscribe({
        next: (center) => {
          // Update the center in the local array
          const index = this.centers.findIndex(c => c.id === center.id);
          if (index !== -1) {
            this.centers[index] = center;
            // Sort centers by fullName to maintain order
            this.centers.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
            // Update the data source to trigger change detection
            this.dataSource.data = [...this.centers];
          }
          this.cancelEditing();
          this.showNotification('Center updated successfully');
        },
        error: (error) => {
          console.error('Error updating center:', error);
          this.showNotification('Failed to update center');
        }
      });
    }
  }

  cancelEditing(): void {
    this.editingCenter = null;
    this.centerForm.reset();
  }

  isEditing(center: Center): boolean {
    return this.editingCenter?.id === center.id;
  }

  getParticipantCounts(center: Center): string {
    const recipientCount = center.recipients?.length || 0;
    const donorCount = center.donors?.length || 0;
    return `${recipientCount} recipients, ${donorCount} donors`;
  }

  runBackfill(): void {
    this.backfillRunning = true;
    this.backfillResult = null;
    this.centerService.backfillParticipantSnapshots().subscribe({
      next: (result) => {
        this.backfillResult = result;
        this.backfillRunning = false;
        this.showNotification(`Backfill complete: ${result.processed} processed, ${result.skipped} skipped, ${result.errors} errors`);
      },
      error: () => {
        this.backfillRunning = false;
        this.showNotification('Backfill failed — check server logs');
      }
    });
  }

  showNotification(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }
}